import { gql } from "graphql-request";

// Create a new batch with pre-signed upload URLs
export const CREATE_CUSTOMS_BATCH = gql`
  mutation CreateCustomsBatch($input: CreateCustomsBatchInput!) {
    createCustomsBatch(input: $input) {
      batchId
      documents {
        docId
        filename
        uploadUrl
        s3Key
      }
    }
  }
`;

// Manually trigger processing for all UPLOADED documents in a batch
export const PROCESS_CUSTOMS_BATCH = gql`
  mutation ProcessCustomsBatch($batchId: ID!, $tenantId: ID) {
    processCustomsBatch(batchId: $batchId, tenantId: $tenantId) {
      processed
      succeeded
      failed
    }
  }
`;

// Get batch status with all documents
export const GET_CUSTOMS_BATCH = gql`
  query CustomsBatch($batchId: ID!) {
    customsBatch(batchId: $batchId) {
      batch {
        _id
        tenantId
        clientId
        createdBy
        totalFiles
        statusCounts {
          uploaded
          textExtracted
          keysExtracted
          ready
          failed
        }
        createdAt
        completedAt
      }
      documents {
        docId
        filename
        status
        error {
          code
          message
          stage
        }
      }
    }
  }
`;

// List batches for tenant
export const GET_CUSTOMS_BATCHES = gql`
  query CustomsBatches($tenantId: ID, $clientId: ID, $pageNo: Int, $pageSize: Int) {
    customsBatches(tenantId: $tenantId, clientId: $clientId, pageNo: $pageNo, pageSize: $pageSize) {
      data {
        _id
        tenantId
        clientId
        createdBy
        totalFiles
        statusCounts {
          uploaded
          textExtracted
          keysExtracted
          ready
          failed
        }
        createdAt
        completedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// List documents with filters
export const GET_CUSTOMS_DOCUMENTS = gql`
  query CustomsDocuments(
    $tenantId: ID
    $clientId: ID
    $batchId: ID
    $status: CustomsDocumentStatus
    $clusterId: ID
    $pageNo: Int
    $pageSize: Int
  ) {
    customsDocuments(
      tenantId: $tenantId
      clientId: $clientId
      batchId: $batchId
      status: $status
      clusterId: $clusterId
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Get single document with download URL
export const GET_CUSTOMS_DOCUMENT = gql`
  query CustomsDocument($docId: ID!, $tenantId: ID) {
    customsDocument(docId: $docId, tenantId: $tenantId) {
      document {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        extractedText
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      downloadUrl
    }
  }
`;

// Search documents by identifier or text
export const SEARCH_CUSTOMS_DOCUMENTS = gql`
  query SearchCustomsDocuments(
    $query: String!
    $tenantId: ID
    $clientId: ID
    $documentType: CustomsDocumentType
    $pageNo: Int
    $pageSize: Int
  ) {
    searchCustomsDocuments(
      query: $query
      tenantId: $tenantId
      clientId: $clientId
      documentType: $documentType
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        batchId
        filename
        mimeType
        sizeBytes
        status
        error {
          code
          message
          stage
        }
        textQualityScore
        pageCount
        identifiers {
          type
          value
          confidence
          extractedBy
        }
        clusterId
        documentType
        createdBy
        createdAt
        updatedAt
        processedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Get cluster with all documents
export const GET_CUSTOMS_CLUSTER = gql`
  query CustomsCluster($clusterId: ID!) {
    customsCluster(clusterId: $clusterId) {
      cluster {
        _id
        tenantId
        clientId
        clusterKeys {
          type
          value
          sourceDocIds
        }
        entryNumbers
        entrySummaryDocIds
        documentIds
        documentCount
        status
        evidence
        createdAt
        updatedAt
      }
      documents {
        _id
        filename
        status
        documentType
        identifiers {
          type
          value
        }
      }
    }
  }
`;

// List clusters with pagination and optional filters
// Use 'identifier' to search by any identifier type (BL, Container, PO, Invoice, Entry)
export const GET_CUSTOMS_CLUSTERS = gql`
  query CustomsClusters(
    $tenantId: ID
    $clientId: ID
    $entryNumber: String
    $identifier: String
    $pageNo: Int
    $pageSize: Int
  ) {
    customsClusters(
      tenantId: $tenantId
      clientId: $clientId
      entryNumber: $entryNumber
      identifier: $identifier
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        tenantId
        clientId
        clusterKeys {
          type
          value
          sourceDocIds
        }
        entryNumbers
        entrySummaryDocIds
        documentIds
        documentCount
        status
        evidence
        createdAt
        updatedAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Delete a customs document
export const DELETE_CUSTOMS_DOCUMENT = gql`
  mutation DeleteCustomsDocument($docId: ID!, $tenantId: ID) {
    deleteCustomsDocument(docId: $docId, tenantId: $tenantId)
  }
`;

// Get the most recent audit for a cluster
export const GET_CLUSTER_AUDIT = gql`
  query ClusterAudit($clusterId: ID!, $tenantId: ID) {
    clusterAudit(clusterId: $clusterId, tenantId: $tenantId) {
      _id
      clusterId
      tenantId
      clientId
      entryNumber
      status
      confidenceScore
      dataCompleteness
      discrepancies {
        id
        severity
        category
        title
        description
        citations {
          documentId
          documentName
          documentType
          excerpt
          pageNumber
          tradeDataLine
          tradeDataField
        }
        recommendation
      }
      analysisText
      thinkingTrail
      documentsAnalyzed
      tradeDataLinesAnalyzed
      modelUsed
      processingTimeMs
      tokenUsage {
        input
        output
      }
      createdAt
      updatedAt
    }
  }
`;

// List audits with filters
export const GET_CLUSTER_AUDITS = gql`
  query ClusterAudits(
    $tenantId: ID
    $clientId: ID
    $status: ClusterAuditStatus
    $pageNo: Int
    $pageSize: Int
  ) {
    clusterAudits(
      tenantId: $tenantId
      clientId: $clientId
      status: $status
      pageNo: $pageNo
      pageSize: $pageSize
    ) {
      data {
        _id
        clusterId
        tenantId
        clientId
        entryNumber
        status
        confidenceScore
        dataCompleteness
        discrepancies {
          id
          severity
          category
          title
        }
        documentsAnalyzed
        tradeDataLinesAnalyzed
        processingTimeMs
        createdAt
      }
      countDocs
      pageNo
      pageSize
    }
  }
`;

// Run AI audit on a cluster
export const RUN_CLUSTER_AUDIT = gql`
  mutation RunClusterAudit($clusterId: ID!, $tenantId: ID) {
    runClusterAudit(clusterId: $clusterId, tenantId: $tenantId) {
      _id
      clusterId
      tenantId
      clientId
      entryNumber
      status
      confidenceScore
      dataCompleteness
      discrepancies {
        id
        severity
        category
        title
        description
        citations {
          documentId
          documentName
          documentType
          excerpt
          pageNumber
          tradeDataLine
          tradeDataField
        }
        recommendation
      }
      analysisText
      thinkingTrail
      documentsAnalyzed
      tradeDataLinesAnalyzed
      modelUsed
      processingTimeMs
      tokenUsage {
        input
        output
      }
      createdAt
      updatedAt
    }
  }
`;
